import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { embedDashboard } from '@superset-ui/embedded-sdk';
import { dashboardService } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import {
  ArrowLeftIcon,
  AlertTriangleIcon,
  Lock
} from 'lucide-react';

function DashboardDetail() {
  const { dashboardId } = useParams(); // This is now the UUID
  const navigate = useNavigate();
  const { notify } = useNotification();
  const dashboardContainerRef = useRef(null);
  
  console.log('DashboardDetail component mounted with dashboardUuid:', dashboardId);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [embeddingUuid, setEmbeddingUuid] = useState(null);
  const [loadingEmbeddingUuid, setLoadingEmbeddingUuid] = useState(false);
  const [embeddingError, setEmbeddingError] = useState(null);

  // Function to fetch embedding UUID for Superset SDK
  const fetchEmbeddingUuid = useCallback(async (dashboardUuid) => {
    try {
      setLoadingEmbeddingUuid(true);
      console.log(`Fetching embedding UUID for dashboard: ${dashboardUuid}`);
      const response = await dashboardService.getEmbeddingUuid(dashboardUuid);
      
      console.log('Embedding UUID API response:', response);
      
      // Handle different response formats
      let uuid;
      if (typeof response === 'string') {
        uuid = response;
      } else if (response && response.embedding_uuid) {
        uuid = response.embedding_uuid;
      } else if (response && response.uuid) {
        uuid = response.uuid;
      } else {
        console.error('Unexpected embedding UUID response format:', response);
        throw new Error('Invalid embedding UUID response format');
      }
      
      console.log('Embedding UUID received:', uuid);
      setEmbeddingUuid(uuid);
      return uuid;
    } catch (error) {
      console.error('Error fetching embedding UUID:', error);
      setEmbeddingError('Failed to fetch embedding configuration');
      throw error;
    } finally {
      setLoadingEmbeddingUuid(false);
    }
  }, []);

  // Function to fetch guest token for Superset embedding
  const fetchGuestToken = useCallback(async () => {
    try {
      console.log(`Fetching guest token for embedding UUID: ${embeddingUuid}`);
      const response = await dashboardService.getGuestToken(embeddingUuid);
      
      // Handle different response formats
      if (typeof response === 'string') {
        return response;
      } else if (response && response.guest_token) {
        return response.guest_token;
      } else if (response && response.token) {
        return response.token;
      } else {
        console.error('Unexpected guest token response format:', response);
        throw new Error('Invalid guest token response format');
      }
    } catch (error) {
      console.error('Error fetching guest token:', error);
      setEmbeddingError('Failed to authenticate dashboard access');
      return '';
    }
  }, [embeddingUuid]);

  // Initialize dashboard embedding
  const initializeEmbedding = useCallback(async () => {
    if (!dashboardContainerRef.current || !dashboard || !embeddingUuid) return;

    try {
      console.log('Initializing Superset embedding for dashboard:', dashboard);
      
      // Log container dimensions for debugging
      const containerRect = dashboardContainerRef.current.getBoundingClientRect();
      console.log('Container dimensions before embedding:', {
        width: containerRect.width,
        height: containerRect.height,
        top: containerRect.top,
        left: containerRect.left
      });
      
      await embedDashboard({
        // Dashboard UUID for Superset embedding (must be the embedding UUID)
        id: embeddingUuid,
        
        // Superset instance URL - this should be configured based on your setup
        supersetDomain: 'https://analytics.plexe.ai',
        
        // Container element for the iframe
        mountPoint: dashboardContainerRef.current,
        
        // Function to fetch guest token
        fetchGuestToken: fetchGuestToken,
        
        // Dashboard UI configuration for embedded experience
        dashboardUiConfig: {
          hideTitle: false,           // Remove Superset title for cleaner integration
          hideTab: true,            // Remove tab bar
          hideChartControls: false, // Keep chart interactions
          filters: {
            expanded: false,         // Start with filters collapsed
            visible: true
          },
          urlParams: {
            standalone: '1',         // Render in standalone mode
            expand: '1'              // Expand to full size
          }
        },
        
        // Additional iframe security options
        iframeSandboxExtras: [
          'allow-scripts',     // Required for dashboard interactivity
          'allow-same-origin', // Required for API calls to Superset
          'allow-forms',       // Required for dashboard filters/interactions
          'allow-popups'       // May be needed for export functionality
        ]
      });
      
      console.log('Dashboard embedded successfully');
      
      // Force iframe to fill container - the SDK might not be setting proper dimensions
      setTimeout(() => {
        const iframe = dashboardContainerRef.current?.querySelector('iframe');
        if (iframe) {
          console.log('Found iframe, applying full-size styles');
          iframe.style.width = '100%';
          iframe.style.height = '100%';
          iframe.style.border = 'none';
          iframe.style.position = 'absolute';
          iframe.style.top = '0';
          iframe.style.left = '0';
          console.log('Iframe dimensions after styling:', {
            width: iframe.style.width,
            height: iframe.style.height,
            clientWidth: iframe.clientWidth,
            clientHeight: iframe.clientHeight
          });
        } else {
          console.warn('No iframe found in container');
        }
      }, 500);
      
    } catch (error) {
      console.error('Error embedding dashboard:', error);
      setEmbeddingError(`Failed to embed dashboard: ${error.message}`);
      
      // Fallback to external link if embedding fails
      if (dashboard.url) {
        console.log('Fallback: redirecting to external dashboard');
        setTimeout(() => {
          const url = `https://analytics.plexe.ai/${dashboard.url}`;
          window.open(url, '_blank', 'noopener,noreferrer');
        }, 3000);
      }
    }
  }, [dashboard, embeddingUuid, fetchGuestToken, dashboardContainerRef]);

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // For now, we'll simulate fetching dashboard details
        // In a real implementation, you might need a specific API to get dashboard by ID
        // or extract the dashboard info from the main dashboards list
        
        // Find dashboard by UUID
        const dashboards = await dashboardService.getDashboards();

        // Ensure dashboards is a valid array
        if (!dashboards || !Array.isArray(dashboards)) {
          console.error('Invalid dashboards response:', dashboards);
          throw new Error('Failed to load dashboards');
        }

        const foundDashboard = dashboards.find(d => d && d.uuid === dashboardId);

        console.log('Available dashboards:', dashboards);
        console.log('Looking for dashboard UUID:', dashboardId);
        console.log('Found dashboard:', foundDashboard);

        if (!foundDashboard) {
          throw new Error('Dashboard not found');
        }
        
        setDashboard(foundDashboard);
        
        // Fetch embedding UUID for the dashboard
        await fetchEmbeddingUuid(foundDashboard.uuid);
        
      } catch (error) {
        console.error('Error loading dashboard:', error);
        setError('Failed to load dashboard details');
        notify.error('Failed to load dashboard. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (dashboardId) {
      loadDashboard();
    } else {
      setError('No dashboard ID provided');
      setLoading(false);
    }
  }, [dashboardId, notify, fetchEmbeddingUuid]);

  // Initialize embedding when dashboard and embedding UUID are loaded
  useEffect(() => {
    if (dashboard && embeddingUuid && !loading && !loadingEmbeddingUuid) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        initializeEmbedding();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [dashboard, embeddingUuid, loading, loadingEmbeddingUuid, initializeEmbedding]);

  // Handle back navigation
  const handleBack = () => {
    navigate('/dashboards');
  };

  // Render loading state
  if (loading) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-shrink-0 px-6 py-4 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleBack}
              className="flex items-center px-3 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 rounded-md transition-all duration-200 transform hover:scale-[1.003] hover:shadow-md hover:-translate-y-0.5"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              Back to Dashboards
            </button>
          </div>
        </div>
        
        <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-slate-700">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 mx-auto border-b-2 border-primary-600 dark:border-primary-500 rounded-full"></div>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-shrink-0 px-6 py-4 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleBack}
              className="flex items-center px-3 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 rounded-md transition-all duration-200 transform hover:scale-[1.003] hover:shadow-md hover:-translate-y-0.5"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              Back to Dashboards
            </button>
          </div>
        </div>
        
        <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-slate-700">
          <div className="text-center">
            <AlertTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Error Loading Dashboard</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{error}</p>
            <button
              onClick={handleBack}
              className="mt-4 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 dark:bg-primary-700 dark:text-white dark:hover:bg-primary-600"
            >
              Return to Dashboards
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header - Single line with Back button and Edit in Analytics */}
      <div className="flex-shrink-0 px-6 py-4 bg-white dark:bg-slate-800">
        <div className="flex items-center justify-between">
          <button
            onClick={handleBack}
            className="flex items-center px-3 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 rounded-md transition-all duration-200 transform hover:scale-[1.003] hover:shadow-md hover:-translate-y-0.5"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Dashboards
          </button>
          
          {/* Edit in Analytics button - Locked */}
          {dashboard?.url && (
            <button
              disabled
              className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm leading-4 font-medium rounded-md text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 cursor-not-allowed opacity-75"
              title="Contact us to enable dashboard editing"
            >
              <Lock className="w-4 h-4" />
              Edit in Analytics
            </button>
          )}
        </div>

        {/* Embedding Error */}
        {embeddingError && (
          <div className="mt-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <div className="flex">
              <AlertTriangleIcon className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                  Embedding Issue
                </h3>
                <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-400">
                  {embeddingError}
                  {dashboard?.url && ' Redirecting to external dashboard in a few seconds...'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Dashboard Container - Takes remaining space */}
      <div className="flex-1 relative bg-white dark:bg-slate-800">
        <div
          ref={dashboardContainerRef}
          id="superset-dashboard-container"
          className="absolute inset-0"
        >
          {/* Loading indicator while embedding */}
          {!embeddingError && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-slate-700">
              <div className="text-center">
                <div className="animate-spin h-8 w-8 mx-auto border-b-2 border-primary-600 dark:border-primary-500 rounded-full"></div>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Loading interactive dashboard...
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DashboardDetail;